import { dataSimples } from '../../../lib/datas'
import type { PrecoDoToken } from '../../../types/creditoDaIa'

/**
 * A formatação de dinheiro da IA — escrita UMA vez, e sempre com a procedência ao lado.
 *
 * **A função exportada é `comProcedencia`, e não `formatarReais`.** É deliberado: não existe caminho
 * neste módulo para renderizar um valor sem dizer de onde ele veio. Um número que só aparece
 * acompanhado de "Microsoft Azure" e do nome de quem declarou o preço não se confunde com medição de
 * contrato — medição de contrato não vem com o nome de um servidor ao lado. É a camada de defesa
 * que sobrevive a alguém tirar um print da tela.
 */

const MOEDAS: Record<string, Intl.NumberFormat> = {}

/** Duas casas, `pt-BR`, na moeda pedida. O arredondamento já veio pronto do servidor. */
export function emMoeda(valor: string | number, moeda = 'BRL'): string {
  MOEDAS[moeda] ??= new Intl.NumberFormat('pt-BR', { style: 'currency', currency: moeda })
  return MOEDAS[moeda].format(Number(valor))
}

const INTEIRO = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 })

/** Unidades de uso, com separador de milhar. */
export function emUnidades(valor: number): string {
  return INTEIRO.format(valor)
}

/** `1,10` vira `US$ 1,10` sem o arredondamento de moeda comer as casas do preço por milhão. */
function porMilhao(valor: string, moeda: string): string {
  const numero = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 6 }).format(
    Number(valor),
  )
  return `${moeda} ${numero}`
}

/**
 * O valor com o preço e a procedência ao lado — a única forma de mostrar dinheiro de IA.
 *
 * @param valor o número que o servidor devolveu, já arredondado
 * @param preco o preço que o produziu
 * @returns o texto formatado e a linha de procedência
 */
export function comProcedencia(valor: string, preco: PrecoDoToken): { valor: string; procedencia: string } {
  const partes = [
    'Microsoft Azure',
    `${porMilhao(preco.precoEntradaPorMilhao, preco.moedaDeCobranca)} / ` +
      `${porMilhao(preco.precoSaidaPorMilhao, preco.moedaDeCobranca)} por milhão`,
  ]
  if (preco.cambio && preco.cambioEm) {
    partes.push(
      `câmbio ${emMoeda(preco.cambio)} (${preco.cambioFonte ?? 'declarado'} de ${dataSimples(preco.cambioEm)})`,
    )
  }
  if (preco.registradoPorNome) {
    partes.push(`declarado por ${preco.registradoPorNome}`)
  }
  return { valor: emMoeda(valor), procedencia: partes.join(' · ') }
}

/*
 * Aqui existia um `porExtenso(data)` próprio — `data.slice(0, 10).split('-')` — escrito para
 * `cambioEm`, que é de fato um `LocalDate`. Ele foi APAGADO, e não corrigido, porque o defeito não
 * era a implementação: era haver duas funções para a mesma coisa e nenhuma dizer para qual dos dois
 * formatos servia. Ela acabou recebendo também `vigenciaInicio`, `vigenciaFim`, `credito.registradoEm`
 * e `lote.solicitadoEm`, que são INSTANTES (sufixo Z) — e um preço encerrado às 22h de 27/08 ficava
 * documentado como "Vale de 28/08/2026 em diante". A tabela de preços é a peça que responde "qual
 * preço governou as chamadas do dia X" numa conferência contra a fatura da Azure, então um dia a mais
 * ali é auditoria errada.
 *
 * Agora há UMA função por formato, as duas em `lib/datas.ts` e cada uma com o javadoc dizendo quando
 * usar: `dataSimples` para data pura (`AAAA-MM-DD`, sem fuso, recortada por regex de propósito) e
 * `data`/`dataHora` para instante (com a conversão para o fuso de Niterói, que ali é necessária).
 */

/** Quantos dias tem a cotação declarada, contra a data que o servidor diz ser hoje. */
export function idadeDoCambio(preco: PrecoDoToken, hoje: string): number | null {
  if (!preco.cambioEm) {
    return null
  }
  const umDia = 24 * 60 * 60 * 1000
  return Math.floor((Date.parse(hoje) - Date.parse(preco.cambioEm.slice(0, 10))) / umDia)
}

/** Acima disso a tela avisa que a cotação está velha. Espelha `DIAS_PARA_CAMBIO_VELHO` do servidor. */
export const DIAS_PARA_CAMBIO_VELHO = 30
